import { useState, useEffect } from "react";
import { Check, X, Zap, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const tiers = [
  {
    name: "Starter",
    price: { monthly: "€5", annual: "€50" },
    credits: 200,
    description: "For professionals who need basic email extraction",
    features: [
      { name: "200 AI credits/month", included: true },
      { name: "Email extraction (from 1 credit)", included: true },
      { name: "Basic dashboard & CSV export", included: true },
      { name: "Community support", included: true },
      { name: "Smart Categories", included: false },
      { name: "Smart Notes", included: false },
      { name: "Field Presets", included: false },
      { name: "API access", included: false },
    ],
    cta: "Start with Starter",
    popular: false,
  },
  {
    name: "Pro",
    price: { monthly: "€19", annual: "€190" },
    credits: 1000,
    description: "The complete AI assistant for power users",
    features: [
      { name: "1,000 AI credits/month", included: true },
      { name: "Email extraction", included: true },
      { name: "Smart Categories (up to 10)", included: true },
      { name: "Smart Notes (up to 10 tasks)", included: true },
      { name: "Field Presets (unlimited)", included: true },
      { name: "Priority support (24h SLA)", included: true },
      { name: "API access & Zapier", included: true },
      { name: "Advanced analytics", included: true },
    ],
    cta: "Get Pro",
    popular: true,
  },
  {
    name: "Enterprise",
    price: { monthly: "€99+", annual: "Custom" },
    credits: 5000,
    description: "For teams who need scale and customization",
    features: [
      { name: "5,000+ AI credits/month", included: true },
      { name: "All Pro features", included: true },
      { name: "Unlimited Smart Categories & Notes", included: true },
      { name: "Batch processing & CRM sync", included: true },
      { name: "Dedicated account manager", included: true },
      { name: "Custom integrations", included: true },
      { name: "SLA-guaranteed support", included: true },
      { name: "Privacy-focused EU processing", included: true },
    ],
    cta: "Contact Sales",
    popular: false,
  },
];

const topupPackages = [
  { name: "Mini", credits: 100, price: 5, discount: null },
  { name: "Standard", credits: 500, price: 18, discount: "10% off" },
  { name: "Power", credits: 2000, price: 55, discount: "30% off" },
  { name: "Agency", credits: 10000, price: 220, discount: "45% off" },
];

export const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/plans');
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="container px-4 py-16 md:py-24">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="mb-4" variant="secondary">
            Transparent Pricing
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Simple pricing. Pay for what you use.
          </h1>
          <p className="text-xl text-muted-foreground mb-8">
            Credit-based system that scales with your workflow. No hidden fees. Cancel anytime.
          </p>
          {!user && (
            <p className="text-sm text-muted-foreground italic">
              Sign up to access subscription plans and manage your account
            </p>
          )}
          
          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-3 bg-muted p-1 rounded-lg">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-4 py-2 rounded-md transition-colors ${
                billingCycle === "monthly"
                  ? "bg-background shadow-sm font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("annual")}
              className={`px-4 py-2 rounded-md transition-colors ${
                billingCycle === "annual"
                  ? "bg-background shadow-sm font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Annual
              <Badge className="ml-2" variant="secondary">
                Save 2 months
              </Badge>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {tiers.map((tier) => (
            <Card
              key={tier.name}
              className={tier.popular ? "border-primary shadow-elevated relative" : ""}
            >
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground">Most Popular</Badge>
                </div>
              )}
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-2xl mb-2">{tier.name}</CardTitle>
                <div className="mb-2">
                  <span className="text-4xl font-bold">
                    {tier.price[billingCycle]}
                  </span>
                  {tier.name !== "Enterprise" && (
                    <>
                      <span className="text-muted-foreground">
                        /{billingCycle === "monthly" ? "month" : "year"}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">
                        plus 19% VAT
                      </p>
                    </>
                  )}
                </div>
                <CardDescription>{tier.description}</CardDescription>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <Coins className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">
                    {tier.credits.toLocaleString()} credits/month
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <Separator />
                <ul className="space-y-3">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      {feature.included ? (
                        <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                      )}
                      <span
                        className={
                          feature.included
                            ? "text-foreground"
                            : "text-muted-foreground line-through"
                        }
                      >
                        {feature.name}
                      </span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full"
                  variant={tier.popular ? "default" : "outline"}
                  asChild
                >
                  <Link to="/auth">{tier.name === "Enterprise" ? "Contact Sales" : "Sign Up to Subscribe"}</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>


        {/* FAQ Preview */}
        <div className="mt-24 max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-4">Have questions?</h2>
          <p className="text-muted-foreground mb-6">
            Check out our FAQ or contact support for help choosing the right plan.
          </p>
          <div className="flex gap-4 justify-center">
            <Button variant="outline" asChild>
              <Link to="/support#faq">View FAQ</Link>
            </Button>
            <Button asChild>
              <Link to="/support">Contact Support</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
